import { StackLayout } from '@euler/app/components/StackLayout';
import { Timestamp } from '@euler/app/components/Timestamp';
import { OrderSummaryInfoField } from '@euler/app/components/order/summary/SummaryInfoField';
import { Typography } from '@euler/app/components/typography';
import { MemberChatButton } from '@euler/app/flows/chat/components/MemberChatButton';
import { MemberChatButtonByName } from '@euler/app/flows/chat/components/MemberChatButtonByName';
import { Avatar } from '@euler/components/Avatar';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { EulerViewProps } from '@euler/components/layout';
import { HStack } from '@euler/components/layout/Stack';
import { kOrderFieldNoValuePlaceHolder } from '@euler/functions/order/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrderFieldVisibility } from '@euler/model';
import { InspectionTaskStatus } from '@euler/model/enum';
import { Maybe } from '@euler/types';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import isBefore from 'date-fns/isBefore';
import { memo } from 'react';

export const ServiceAgentInfoRow = memo(
  ({
    isMainOrder,
    serviceAgentName,
    serviceAgentMemberId,
    serviceAgentChatByName,
    createdAt,
    status,
    finishedAt,
    estimatedFinishTime,
    estimatedFinishTimeVisibility,
    ...props
  }: EulerViewProps & {
    isMainOrder: boolean;
    serviceAgentName: string;
    /** Org member id of the advisor — when set, shows a "start chat" button
     * (the caller opts in; not every order-summary surface wants it). */
    serviceAgentMemberId?: number;
    /** Opt-in: show the advisor chat button resolved by NAME, for surfaces that
     * lack the member id (e.g. a service record). Ignored when
     * `serviceAgentMemberId` is set. */
    serviceAgentChatByName?: boolean;
    createdAt: Date | string;
    status: InspectionTaskStatus;
    finishedAt?: Maybe<string | Date>;
    estimatedFinishTime?: Maybe<string | Date>;
    estimatedFinishTimeVisibility?: OrderFieldVisibility;
  }) => {
    const { rpx } = useResponsiveContext();

    const hasTimedOut =
      estimatedFinishTime != null &&
      isBefore(dateFromValue(estimatedFinishTime), new Date());

    return (
      <HStack
        justifyContent="flex-start"
        alignItems="center"
        flexWrap="nowrap"
        mt={0}
        {...props}
      >
        <Avatar
          name={serviceAgentName}
          width={rpx(18)}
          height={rpx(18)}
          borderRadius={rpx(9)}
        />
        <Typography.Label light size={rpx(12)} color="#555" ml={rpx(4)}>
          {serviceAgentName}
        </Typography.Label>
        {serviceAgentMemberId != null ? (
          <MemberChatButton
            orgMemberId={serviceAgentMemberId}
            size={rpx(14)}
            style={{ marginLeft: rpx(4) }}
          />
        ) : serviceAgentChatByName ? (
          <MemberChatButtonByName
            name={serviceAgentName}
            size={rpx(14)}
            style={{ marginLeft: rpx(4) }}
          />
        ) : null}
        <Timestamp
          timestamp={createdAt}
          value={
            status === InspectionTaskStatus.Finished
              ? differenceInSeconds(
                  dateFromValue(finishedAt),
                  dateFromValue(createdAt),
                  { roundingMethod: 'round' },
                )
              : undefined
          }
          label={status === InspectionTaskStatus.Finished ? '耗时' : undefined}
          css={`
            margin-left: ${rpx(8)}px;
          `}
          labelStyle={{
            color: '#555',
            fontSize: rpx(12),
          }}
        />
        {status === InspectionTaskStatus.Finished ? (
          <HStack justifyContent="flex-start" ml={rpx(8)}>
            <Icon name="check" color={StatusColors.Success} size={rpx(16)} />
            <Typography.Label light size={rpx(12)} color={StatusColors.Success}>
              {isMainOrder ? '本次服务已完成' : '当前子订单已完成'}
            </Typography.Label>
          </HStack>
        ) : (
          <OrderSummaryInfoField
            value={estimatedFinishTime}
            visibility={estimatedFinishTimeVisibility}
          >
            <StackLayout direction="row" alignItems="center" ml={rpx(4)}>
              <Typography.Text
                family={hasTimedOut ? 'medium' : 'light'}
                size={rpx(12)}
                color={hasTimedOut ? StatusColors.Warn : '#555'}
                lineHeight={rpx(18)}
                letterSpacing={rpx(-0.5)}
              >
                {`预计交车: ${
                  estimatedFinishTime
                    ? formatDateTime(estimatedFinishTime)
                    : kOrderFieldNoValuePlaceHolder
                }`}
              </Typography.Text>
              {hasTimedOut ? (
                <StackLayout direction="row" ml={rpx(10)} alignItems="center">
                  <Icon
                    name="alert-octagon"
                    size={rpx(14)}
                    color={StatusColors.Warn}
                    css={`
                      top: ${rpx(1)}px;
                    `}
                  />
                  <Typography.Text
                    color={StatusColors.Warn}
                    size={rpx(12)}
                    lineHeight={rpx(18)}
                    medium
                  >
                    已超时
                  </Typography.Text>
                </StackLayout>
              ) : null}
            </StackLayout>
          </OrderSummaryInfoField>
        )}
      </HStack>
    );
  },
);
